import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  Tabs,
  KpiCard,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  useCommStats,
  useCommLogs,
  ChannelPill,
  StatusBadge,
} from '@/features/communications';
import type { CommunicationLog, CommChannel, CommLogStatus } from '@/features/communications';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

const CHANNEL_TABS = [
  { id: '', label: 'Overview' },
  { id: 'WHATSAPP', label: 'WhatsApp' },
  { id: 'EMAIL', label: 'Email' },
  { id: 'SMS', label: 'SMS' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SENT', label: 'Sent' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'FAILED', label: 'Failed' },
];

// The channel-tabs/filter panel is a backdrop-blur glass card, which creates
// its own stacking context and traps the status Select's z-50 inside the
// panel. Without an explicit z-index, the opaque log table below — including
// its sticky header at z-10 — paints over the open dropdown, so the status
// options collide with the table's TYPE column/rows. `relative z-20` lifts the
// panel above the table's sticky header so the dropdown overlays cleanly.
// Regression: GitHub issue #137.
export const FILTER_BAR_CLASS =
  'relative z-20 rounded-xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-md p-4 sm:p-5 mb-6';

type Row = CommunicationLog;

// Quotation sends are stored as type INVOICE (chk_comm_logs_type has no
// QUOTATION) — label them by their reference instead.
function logTypeLabel(row: Row): string {
  return row.reference_type === 'QUOTATION' ? 'Quotation' : row.type.replace(/_/g, ' ');
}

// A link share has no recipient; rows logged before CU4-007 hold the quote's
// approval URL (a bearer link) — never show it.
function maskRecipient(recipient: string): string {
  if (recipient === 'BROADCAST') return 'Broadcast';
  if (recipient === 'LINK' || /^https?:\/\//.test(recipient)) return 'Public link';
  if (recipient.includes('@')) {
    const [local, domain] = recipient.split('@');
    return `${local[0]}***@${domain}`;
  }
  if (recipient.startsWith('+') && recipient.length > 6) {
    return `${recipient.slice(0, 4)}****${recipient.slice(-4)}`;
  }
  return recipient;
}

const columns: ColumnDef<Row>[] = [
  {
    key: 'type',
    header: 'Type',
    render: (_v, row) => (
      <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
        {logTypeLabel(row)}
      </span>
    ),
  },
  {
    key: 'channel',
    header: 'Channel',
    render: (_v, row) => <ChannelPill channel={row.channel} />,
  },
  {
    key: 'recipient',
    header: 'Recipient',
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
        {maskRecipient(row.recipient)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <StatusBadge status={row.status} variant="log" />,
  },
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
        {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        {' '}
        {new Date(row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </span>
    ),
  },
];

function CommunicationHubContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const isOwnerPlus = roleLevel >= ROLE_LEVELS.OWNER;
  const isManagerPlus = roleLevel >= ROLE_LEVELS.MANAGER;

  const page = Number(searchParams.get('page') ?? '1');
  const channel = (searchParams.get('channel') ?? '') as CommChannel | '';
  const status = (searchParams.get('status') ?? '') as CommLogStatus | '';
  const activeTab = channel;

  const { data: stats, isLoading: statsLoading } = useCommStats();
  const { data: logsData, isLoading: logsLoading, isFetching } = useCommLogs({ page, channel, status });

  const rows = useMemo<Row[]>(() => logsData?.data ?? [], [logsData]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  // Keyboard: / → focus search
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(20,184,166,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Digital Communications
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Communication Hub</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Send invoices, run campaigns, publish deals — all from one place.
            </p>
          </div>
          {isManagerPlus ? (
            <div className="flex flex-wrap items-center gap-2">
              {isOwnerPlus ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate('/communications/deal-of-day')}
                  className="!bg-white/15 hover:!bg-white/25 !text-white !border-white/30 backdrop-blur-sm"
                >
                  Deal of the Day
                </Button>
              ) : null}
              <Button
                variant="primary"
                onClick={() => navigate('/communications/campaigns')}
                className="!bg-white !text-teal-700 hover:!bg-white/90"
              >
                + New Campaign
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* KPI Cards — using KpiCard component */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={100} className="rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title="Messages Today"
            value={(stats?.messages_today ?? 0).toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
            className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Delivery Rate"
            value={`${stats?.delivery_rate ?? 0}%`}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            className="bg-gradient-to-br from-success-50 to-emerald-50 dark:from-success-900/20 dark:to-emerald-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Open Rate"
            value={stats?.open_rate != null ? `${stats.open_rate}%` : 'N/A'}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
            className="bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Failed"
            value={(stats?.failed_count ?? 0).toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            className="bg-gradient-to-br from-danger-50 to-red-50 dark:from-danger-900/20 dark:to-red-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
        </div>
      )}

      {/* Quick Actions — MANAGER+ can send promos, OWNER+ gets full control */}
      {isManagerPlus ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate('/communications/invoice-preview')}
            className="group rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-5 text-left hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white mb-3">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-brand-600 dark:group-hover:text-brand-300 transition-colors">
              Send Invoice
            </p>
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">Deliver a digital invoice via WhatsApp or Email</p>
          </button>

          {isOwnerPlus ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/communications/deal-of-day')}
                className="group rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-5 text-left hover:shadow-lg transition-all duration-200 shadow-md"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white mb-3">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-amber-600 dark:group-hover:text-amber-300 transition-colors">
                  Deal of the Day
                </p>
                <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">Create a promotional deal and push to customers</p>
              </button>

              <button
                type="button"
                onClick={() => navigate('/communications/templates')}
                className="group rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-5 text-left hover:shadow-lg transition-all duration-200 shadow-md"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white mb-3">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-teal-600 dark:group-hover:text-teal-300 transition-colors">
                  Templates
                </p>
                <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">Manage your message templates for all channels</p>
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Channel tabs + filters */}
      <div className={FILTER_BAR_CLASS}>
        <Tabs
          items={CHANNEL_TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={activeTab}
          onChange={(id) => setParam('channel', id)}
        />
        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="w-full lg:w-44">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
            />
          </div>
        </div>
      </div>

      {/* Data */}
      {logsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No messages yet"
            description="Send your first invoice or create a campaign to get started."
            action={
              isManagerPlus
                ? { label: 'Send Invoice', onClick: () => navigate('/communications/invoice-preview') }
                : undefined
            }
            icon={
              <svg className="h-20 w-20 text-teal-300 dark:text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Recent communications">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={isFetching}
            totalCount={logsData?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => setParam('page', String(p))}
            stickyHeader
          />
        </section>
      )}
    </div>
  );
}

export function CommunicationHubPage() {
  return (
    <PageContainer
      title="Communications"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Communications' }]}
    >
      <ErrorBoundary resetKey="comm-hub">
        <CommunicationHubContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
